import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import AuthShell from 'components/layout/Auth/AuthShell';

import ChannelStep from 'components/layout/Auth/ChannelStep';
import TokenStep, { TokenStatus } from 'components/layout/Auth/TokenStep';
import { useCountdown } from 'components/layout/Auth/useCountdown';

import AlterarSenha from './AlterarSenha';
import Concluido from './Concluido';
import InformarEmail from './InformarEmail';
import {
  Channel,
  changePassword,
  getChannels,
  parseError,
  sendCode,
  validateCode,
} from './services';

type Passo = 'email' | 'canal' | 'codigo' | 'senha' | 'concluido';

const SEGUNDOS_DO_CODIGO = 60;

const ERRO_GENERICO = 'Ocorreu um erro interno na aplicação';

const EsqueciSenha = (): JSX.Element => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const tokenDaUrl = params.get('token');
  const userIdDaUrl = params.get('user-id');
  const veioDeLink = !!tokenDaUrl && !!userIdDaUrl;

  const [passo, setPasso] = useState<Passo>(veioDeLink ? 'senha' : 'email');
  const [login, setLogin] = useState('');
  const [userId, setUserId] = useState(userIdDaUrl ?? '');
  const [channels, setChannels] = useState<Channel[]>([]);
  const [contactId, setContactId] = useState('');
  const [codigo, setCodigo] = useState(tokenDaUrl ?? '');
  const [status, setStatus] = useState<TokenStatus>('aguardando');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const { restante, expirado, restart } = useCountdown(SEGUNDOS_DO_CODIGO);

  useEffect(() => {
    setErro(null);
  }, [passo]);

  const canalEscolhido = channels.find((c) => c.contactId === contactId);

  const buscarCanais = useCallback(async (valor: string) => {
    setLoading(true);
    setErro(null);
    try {
      const dados = await getChannels(valor);
      setUserId(dados.userId);
      setChannels(dados.channels ?? []);
      setContactId(dados.channels?.[0]?.contactId ?? '');
      setPasso('canal');
    } catch (error) {
      const { message } = parseError(error);
      setErro(message ?? ERRO_GENERICO);
    }
    setLoading(false);
  }, []);

  const enviarCodigo = useCallback(
    async (proximoPasso: Passo) => {
      setLoading(true);
      setErro(null);
      setCodigo('');
      setStatus('aguardando');
      try {
        await sendCode(login, contactId);
        restart();
        setPasso(proximoPasso);
      } catch (error) {
        const { message } = parseError(error);
        setErro(message ?? ERRO_GENERICO);
      }
      setLoading(false);
    },
    [login, contactId, restart]
  );

  const confirmarCodigo = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      await validateCode(userId, codigo);
      setStatus('validado');
      setPasso('senha');
    } catch (error) {
      setStatus('invalido');
    }
    setLoading(false);
  }, [userId, codigo]);

  const gravarSenha = useCallback(
    async (senha: string) => {
      setLoading(true);
      setErro(null);
      try {
        await changePassword(userId, codigo, senha);
        setPasso('concluido');
      } catch (error) {
        const { message } = parseError(error);
        setErro(message ?? ERRO_GENERICO);
      }
      setLoading(false);
    },
    [userId, codigo]
  );

  const tituloCanal = (
    <>
      Por onde deseja receber
      <br />
      instruções para a nova senha?
    </>
  );

  const conteudo = ((): React.ReactNode => {
    if (passo === 'canal') {
      return (
        <ChannelStep
          title={tituloCanal}
          channels={channels}
          contactId={contactId}
          loading={loading}
          erro={erro}
          emptyMessage="Não encontramos um canal disponível para enviar o código. Entre em contato com a Central de Atendimento para recuperar seu acesso."
          onChangeContact={setContactId}
          onSubmit={() => enviarCodigo('codigo')}
          onBack={() => setPasso('email')}
        />
      );
    }

    if (passo === 'codigo') {
      return (
        <TokenStep
          title="Código enviado!"
          subtitle={
            canalEscolhido?.contactType === 'PHONE'
              ? 'Digite o código que você recebeu no celular (token):'
              : 'Digite o código que você recebeu no e-mail (token):'
          }
          codigo={codigo}
          status={status}
          restante={restante}
          expirado={expirado}
          loading={loading}
          onChangeCodigo={(valor) => {
            setCodigo(valor);
            if (status === 'invalido') setStatus('aguardando');
          }}
          onConfirmar={confirmarCodigo}
          onReenviar={() => enviarCodigo('codigo')}
          onBack={() => setPasso('canal')}
          onTrocarCanal={() => setPasso('canal')}
        />
      );
    }

    if (passo === 'senha') {
      return (
        <AlterarSenha
          loading={loading}
          erro={erro}
          onSubmit={gravarSenha}
          onBack={() => setPasso(veioDeLink ? 'email' : 'codigo')}
        />
      );
    }

    if (passo === 'concluido') return <Concluido />;

    return (
      <InformarEmail
        login={login}
        loading={loading}
        erro={erro}
        onChangeLogin={(valor) => {
          setLogin(valor);
          setErro(null);
        }}
        onSubmit={buscarCanais}
      />
    );
  })();

  return <AuthShell>{conteudo}</AuthShell>;
};

export default EsqueciSenha;
